"use client";

import { cn } from "@/lib/cn";

/**
 * The app's on/off switch, in the tenant's colour when on.
 *
 * A real `<button role="switch">` rather than the legacy `<div>` with a hand-rolled
 * `tabIndex` and Enter/Space handler: a button already is focusable and already
 * responds to both keys, and the div version could be focused but not activated by
 * assistive technology that clicks rather than sends keys.
 *
 * The label is required. Legacy's switch had none at all, so it was announced as
 * "switch" with nothing saying what it switched.
 */
export function ToggleSwitch({
  isActive,
  onChange,
  label,
}: {
  isActive: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isActive}
      aria-label={label}
      onClick={onChange}
      className={cn(
        "relative mr-1 block h-[18px] w-[34px] rounded-full shadow-inner shrink-0",
        "bg-gray-300 dark:bg-white",
        "focus:outline focus:outline-2 focus:outline-blue-500",
        // The brand colour is a CSS variable rather than an inline style, so the
        // switch follows a theme change without re-rendering.
        isActive && "bg-[var(--primary)] dark:bg-[var(--primary)]"
      )}
    >
      <span
        className={cn(
          "dot absolute left-[2px] top-0.5 h-[14px] w-[14px] rounded-full",
          "bg-white transition-transform dark:bg-black",
          isActive && "translate-x-[16px]"
        )}
      />
    </button>
  );
}
